import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState(null);

  const lastUpdated = "January 15, 2025";

  const sections = [
    { id: 'acceptance', title: '1. Acceptance of Terms', anchor: 'acceptance' },
    { id: 'services', title: '2. Services Description', anchor: 'services' },
    { id: 'user-responsibilities', title: '3. User Responsibilities', anchor: 'user-responsibilities' },
    { id: 'intellectual-property', title: '4. Intellectual Property Rights', anchor: 'intellectual-property' },
    { id: 'payment', title: '5. Payment Terms', anchor: 'payment' },
    { id: 'confidentiality', title: '6. Confidentiality', anchor: 'confidentiality' },
    { id: 'warranties', title: '7. Warranties and Disclaimers', anchor: 'warranties' },
    { id: 'limitation', title: '8. Limitation of Liability', anchor: 'limitation' },
    { id: 'indemnification', title: '9. Indemnification', anchor: 'indemnification' },
    { id: 'termination', title: '10. Termination', anchor: 'termination' },
    { id: 'governing-law', title: '11. Governing Law', anchor: 'governing-law' },
    { id: 'changes', title: '12. Changes to Terms', anchor: 'changes' },
    { id: 'contact', title: '13. Contact Information', anchor: 'contact' }
  ];

  const scrollToSection = (anchor) => {
    const element = document.getElementById(anchor);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(anchor);
    }
  };

  return (
    <>
      <SEO 
        title="Terms of Service - Legal Terms & Conditions"
        description="Read Abacies Logiciels' Terms of Service governing the use of our AI solutions, custom software development, and digital transformation services."
        keywords="terms of service, legal terms, service agreement, terms and conditions, user agreement, service terms"
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              Terms of <span className="text-gradient">Service</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed animate-slide-up">
              Please read these terms carefully before using our services
            </p>
            <div className="mt-6 inline-block px-4 py-2 bg-blush-light/30 rounded-lg">
              <p className="text-sm text-graphite">
                <strong>Last Updated:</strong> {lastUpdated}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="sticky top-20">
                <div className="card bg-alabaster">
                  <h3 className="text-lg font-semibold mb-4 text-charcoal">Table of Contents</h3>
                  <nav className="space-y-2">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.anchor)}
                        className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-all duration-200 ${
                          activeSection === section.anchor
                            ? 'bg-primary-red text-white font-semibold'
                            : 'text-titanium hover:bg-white hover:text-primary-red'
                        }`}
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Quick Links */}
                <div className="card mt-6">
                  <h3 className="text-lg font-semibold mb-4 text-charcoal">Related Legal</h3>
                  <div className="space-y-2 text-sm">
                    <Link to="/legal/privacy" className="block text-primary-red hover:underline">
                      Privacy Policy
                    </Link>
                    <Link to="/legal/cookies" className="block text-primary-red hover:underline">
                      Cookie Policy
                    </Link>
                    <Link to="/legal/data-security" className="block text-primary-red hover:underline">
                      Data Security
                    </Link>
                    <Link to="/legal/gdpr" className="block text-primary-red hover:underline">
                      GDPR Compliance
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3">
              <div className="prose prose-lg max-w-none">
                {/* Introduction */}
                <div className="mb-12 p-6 bg-blush-light/20 rounded-xl border-l-4 border-primary-red">
                  <p className="text-titanium leading-relaxed mb-0">
                    These Terms of Service ("Terms") govern your access to and use of the services, products, 
                    and website provided by Abacies Logiciels ("Abacies," "we," "us," or "our"). By accessing 
                    or using our services, you agree to be bound by these Terms. If you do not agree to these 
                    Terms, please do not use our services.
                  </p>
                </div>

                {/* Section 1 */}
                <div id="acceptance" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">1. Acceptance of Terms</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      By accessing, browsing, or using the Abacies Logiciels website or any of our services, 
                      you acknowledge that you have read, understood, and agree to be bound by these Terms of 
                      Service and all applicable laws and regulations.
                    </p>
                    <p>
                      These Terms constitute a legally binding agreement between you (either an individual or 
                      entity) and Abacies Logiciels. If you are entering into these Terms on behalf of a company 
                      or other legal entity, you represent that you have the authority to bind such entity to 
                      these Terms.
                    </p>
                    <p>
                      We reserve the right to modify these Terms at any time. Your continued use of our services 
                      following any changes indicates your acceptance of the new Terms.
                    </p>
                  </div>
                </div>

                {/* Section 2 */}
                <div id="services" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">2. Services Description</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      Abacies Logiciels provides enterprise-grade technology solutions including but not limited to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Artificial Intelligence and Machine Learning solutions</li>
                      <li>Custom software development and application design</li>
                      <li>Data analytics and business intelligence services</li>
                      <li>Cloud infrastructure implementation and management</li>
                      <li>Digital transformation consulting and strategy</li>
                      <li>Cybersecurity solutions and assessment</li>
                      <li>Process automation and system integration</li>
                      <li>Managed IT services and technical support</li>
                    </ul>
                    <p>
                      The specific scope, deliverables, timelines, and pricing for services will be detailed 
                      in individual service agreements, statements of work, or project proposals ("Service 
                      Agreements") which supplement these Terms.
                    </p>
                    <p>
                      We reserve the right to modify, suspend, or discontinue any aspect of our services at 
                      any time, with or without notice, though we will make reasonable efforts to notify 
                      clients of material changes affecting active projects.
                    </p>
                  </div>
                </div>

                {/* Section 3 */}
                <div id="user-responsibilities" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">3. User Responsibilities</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      As a user of our services, you agree to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Provide accurate, current, and complete information during registration and engagement</li>
                      <li>Maintain the security and confidentiality of your account credentials</li>
                      <li>Notify us immediately of any unauthorized access or security breaches</li>
                      <li>Use our services only for lawful purposes and in accordance with these Terms</li>
                      <li>Comply with all applicable local, state, national, and international laws</li>
                      <li>Not engage in any activity that interferes with or disrupts our services</li>
                      <li>Provide timely feedback, approvals, and information necessary for project completion</li>
                      <li>Respect intellectual property rights and confidential information</li>
                      <li>Not reverse engineer, decompile, or attempt to extract source code from our solutions</li>
                    </ul>
                    <p>
                      You are responsible for all activities that occur under your account and for ensuring 
                      that all persons who access our services through your account are aware of and comply 
                      with these Terms.
                    </p>
                  </div>
                </div>

                {/* Section 4 */}
                <div id="intellectual-property" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">4. Intellectual Property Rights</h2>
                  <div className="space-y-4 text-titanium">
                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">4.1 Abacies Property</h3>
                    <p>
                      All content, features, functionality, software, tools, frameworks, methodologies, and 
                      materials available through our services, including but not limited to text, graphics, 
                      logos, icons, images, audio clips, digital downloads, data compilations, and software, 
                      are the exclusive property of Abacies Logiciels or our licensors and are protected by 
                      international copyright, trademark, patent, trade secret, and other intellectual property laws.
                    </p>
                    
                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">4.2 Custom Deliverables</h3>
                    <p>
                      Unless otherwise specified in a Service Agreement:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Client retains ownership of their pre-existing data, content, and intellectual property
                      </li>
                      <li>
                        Custom software, applications, and solutions developed specifically for Client will be 
                        owned by Client upon full payment, subject to any third-party licenses
                      </li>
                      <li>
                        Abacies retains ownership of pre-existing tools, frameworks, libraries, methodologies, 
                        and general knowledge used in delivering services
                      </li>
                      <li>
                        Abacies retains the right to use generic concepts, techniques, and know-how learned 
                        during engagement for other projects
                      </li>
                    </ul>

                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">4.3 License Grant</h3>
                    <p>
                      Subject to your compliance with these Terms and payment of applicable fees, Abacies grants 
                      you a limited, non-exclusive, non-transferable, revocable license to access and use our 
                      services and deliverables solely for your internal business purposes.
                    </p>
                  </div>
                </div>

                {/* Section 5 */}
                <div id="payment" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">5. Payment Terms</h2>
                  <div className="space-y-4 text-titanium">
                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">5.1 Fees and Pricing</h3>
                    <p>
                      Service fees, pricing structures, and payment schedules will be specified in individual 
                      Service Agreements. All fees are quoted in United States Dollars (USD) unless otherwise 
                      specified.
                    </p>

                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">5.2 Payment Schedule</h3>
                    <p>
                      Unless otherwise agreed in writing:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Project deposits or initial payments are due upon contract execution</li>
                      <li>Milestone payments are due within 15 days of invoice receipt</li>
                      <li>Recurring service fees are due in advance of each billing period</li>
                      <li>Final payments are due before final deliverable release</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">5.3 Late Payments</h3>
                    <p>
                      Late payments may incur interest charges at a rate of 1.5% per month (18% annually) or 
                      the maximum rate permitted by law, whichever is less. Abacies reserves the right to 
                      suspend services for accounts with payments more than 30 days overdue.
                    </p>

                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">5.4 Taxes</h3>
                    <p>
                      All fees are exclusive of applicable taxes, duties, or similar governmental assessments, 
                      including VAT, GST, sales tax, or withholding taxes. You are responsible for paying all 
                      such taxes except those based on Abacies' net income.
                    </p>

                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">5.5 Refunds</h3>
                    <p>
                      Payments made for services rendered are generally non-refundable. Refund requests will 
                      be evaluated on a case-by-case basis and are subject to the terms of the specific Service 
                      Agreement.
                    </p>
                  </div>
                </div>

                {/* Section 6 */}
                <div id="confidentiality" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">6. Confidentiality</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      Both parties acknowledge that during the course of engagement, they may have access to 
                      confidential information belonging to the other party ("Confidential Information"). 
                      Confidential Information includes, but is not limited to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Technical data, source code, algorithms, and system architectures</li>
                      <li>Business strategies, financial information, and customer data</li>
                      <li>Trade secrets, proprietary methodologies, and know-how</li>
                      <li>Any information marked as confidential or that reasonably should be considered confidential</li>
                    </ul>
                    <p>
                      Each party agrees to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Maintain confidentiality and not disclose Confidential Information to third parties</li>
                      <li>Use Confidential Information solely for the purposes of performing services</li>
                      <li>Protect Confidential Information using the same degree of care used for own information</li>
                      <li>
                        Promptly notify the other party of any unauthorized use or disclosure of Confidential Information
                      </li>
                    </ul>
                    <p>
                      These confidentiality obligations survive termination of the service relationship for a 
                      period of five (5) years, except for trade secrets which remain confidential indefinitely.
                    </p>
                  </div>
                </div>

                {/* Section 7 */}
                <div id="warranties" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">7. Warranties and Disclaimers</h2>
                  <div className="space-y-4 text-titanium">
                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">7.1 Limited Warranty</h3>
                    <p>
                      Abacies warrants that services will be performed in a professional and workmanlike manner 
                      in accordance with industry standards. Custom software deliverables will substantially 
                      conform to documented specifications for a period of ninety (90) days from delivery.
                    </p>

                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">7.2 Disclaimer</h3>
                    <div className="p-4 bg-coral-mist/10 rounded-lg border border-coral-mist/30">
                      <p className="font-semibold text-charcoal mb-2">IMPORTANT LEGAL NOTICE:</p>
                      <p className="text-sm">
                        EXCEPT AS EXPRESSLY PROVIDED IN SECTION 7.1, OUR SERVICES AND ALL CONTENT, MATERIALS, 
                        INFORMATION, SOFTWARE, AND OTHER SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" 
                        BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT 
                        PERMISSIBLE UNDER APPLICABLE LAW, ABACIES DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, 
                        INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A 
                        PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY, AND AVAILABILITY.
                      </p>
                    </div>
                    <p>
                      We do not warrant that our services will be uninterrupted, timely, secure, or error-free, 
                      or that defects will be corrected. We do not warrant the accuracy or completeness of any 
                      content or information provided through our services.
                    </p>
                  </div>
                </div>

                {/* Section 8 */}
                <div id="limitation" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">8. Limitation of Liability</h2>
                  <div className="space-y-4 text-titanium">
                    <div className="p-4 bg-coral-mist/10 rounded-lg border border-coral-mist/30">
                      <p className="font-semibold text-charcoal mb-2">LIMITATION OF LIABILITY:</p>
                      <p className="text-sm mb-3">
                        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ABACIES LOGICIELS, 
                        ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY 
                        INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT 
                        LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
                      </p>
                      <ul className="list-disc pl-6 space-y-1 text-sm">
                        <li>Your access to or use of or inability to access or use our services</li>
                        <li>Any conduct or content of any third party on the services</li>
                        <li>Any content obtained from the services</li>
                        <li>Unauthorized access, use, or alteration of your transmissions or content</li>
                      </ul>
                    </div>
                    <p>
                      In no event shall Abacies' total aggregate liability for all claims arising out of or 
                      relating to these Terms or the services exceed the greater of: (a) the total amount paid 
                      by you to Abacies in the twelve (12) months preceding the claim, or (b) one hundred 
                      dollars ($100).
                    </p>
                    <p>
                      Some jurisdictions do not allow the exclusion or limitation of certain damages, so some 
                      of the above limitations may not apply to you. In such cases, our liability will be 
                      limited to the maximum extent permitted by law.
                    </p>
                  </div>
                </div>

                {/* Section 9 */}
                <div id="indemnification" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">9. Indemnification</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      You agree to defend, indemnify, and hold harmless Abacies Logiciels, its affiliates, 
                      officers, directors, employees, agents, and licensors from and against any and all 
                      claims, damages, obligations, losses, liabilities, costs, debts, and expenses (including 
                      attorney's fees) arising from:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Your use of and access to the services</li>
                      <li>Your violation of any term of these Terms</li>
                      <li>Your violation of any third-party right, including any intellectual property right</li>
                      <li>Any claim that your content caused damage to a third party</li>
                      <li>Your breach of any applicable laws or regulations</li>
                    </ul>
                    <p>
                      This indemnification obligation will survive the termination of these Terms and your use 
                      of the services. Abacies reserves the right to assume exclusive defense and control of 
                      any matter subject to indemnification, in which case you will cooperate in asserting any 
                      available defenses.
                    </p>
                  </div>
                </div>

                {/* Section 10 */}
                <div id="termination" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">10. Termination</h2>
                  <div className="space-y-4 text-titanium">
                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">10.1 Termination by Either Party</h3>
                    <p>
                      Either party may terminate the service relationship by providing written notice in 
                      accordance with the terms specified in the applicable Service Agreement. Unless otherwise 
                      specified, standard termination requires thirty (30) days written notice.
                    </p>

                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">10.2 Termination for Cause</h3>
                    <p>
                      Abacies may immediately terminate or suspend your access to services without prior notice 
                      or liability if:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>You breach any provision of these Terms</li>
                      <li>Payment obligations are more than thirty (30) days overdue</li>
                      <li>Your actions create security risks or legal liability</li>
                      <li>You engage in fraudulent or illegal activities</li>
                      <li>Required by law or court order</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">10.3 Effect of Termination</h3>
                    <p>
                      Upon termination:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Your right to access and use the services immediately ceases</li>
                      <li>
                        You remain liable for all fees and charges incurred prior to termination, including 
                        work completed but not yet invoiced
                      </li>
                      <li>
                        Provisions regarding confidentiality, intellectual property, limitation of liability, 
                        and indemnification survive termination
                      </li>
                      <li>
                        Abacies will return or destroy your confidential information as requested, subject to 
                        legal retention requirements
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Section 11 */}
                <div id="governing-law" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">11. Governing Law and Dispute Resolution</h2>
                  <div className="space-y-4 text-titanium">
                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">11.1 Governing Law</h3>
                    <p>
                      These Terms shall be governed by and construed in accordance with the laws of the State 
                      of California, United States, without regard to its conflict of law provisions. The 
                      United Nations Convention on Contracts for the International Sale of Goods does not apply.
                    </p>

                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">11.2 Dispute Resolution</h3>
                    <p>
                      In the event of any dispute, controversy, or claim arising out of or relating to these 
                      Terms or the services:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        The parties agree to first attempt to resolve the dispute through good faith negotiations
                      </li>
                      <li>
                        If negotiations fail within thirty (30) days, either party may initiate mediation with 
                        a mutually agreed-upon mediator
                      </li>
                      <li>
                        If mediation is unsuccessful, disputes shall be resolved through binding arbitration 
                        under the rules of the American Arbitration Association
                      </li>
                    </ul>

                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">11.3 Jurisdiction and Venue</h3>
                    <p>
                      Any judicial proceedings to enforce an arbitration award or in matters where arbitration 
                      is not required shall be brought exclusively in the federal or state courts located in 
                      San Francisco County, California, and you consent to the personal jurisdiction of such courts.
                    </p>

                    <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">11.4 Class Action Waiver</h3>
                    <p>
                      You agree that any arbitration or proceeding shall be limited to the dispute between you 
                      and Abacies individually. To the fullest extent permitted by law, no arbitration or 
                      proceeding shall be joined with any other, and there is no right or authority for any 
                      dispute to be arbitrated on a class-action basis.
                    </p>
                  </div>
                </div>

                {/* Section 12 */}
                <div id="changes" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">12. Changes to Terms</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      We reserve the right to modify or replace these Terms at any time at our sole discretion. 
                      Material changes will be notified to active clients through:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Email notification to the address on file</li>
                      <li>Prominent notice on our website</li>
                      <li>In-application notification where applicable</li>
                    </ul>
                    <p>
                      Material changes will become effective thirty (30) days after notification, unless a 
                      shorter period is required by law or circumstances. Your continued use of services after 
                      the effective date constitutes acceptance of the modified Terms.
                    </p>
                    <p>
                      If you do not agree to the modified Terms, you must discontinue use of the services and 
                      may terminate your agreement in accordance with Section 10.
                    </p>
                    <p>
                      The "Last Updated" date at the top of these Terms indicates when the most recent changes 
                      were made. We encourage you to review these Terms periodically.
                    </p>
                  </div>
                </div>

                {/* Section 13 */}
                <div id="contact" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">13. Contact Information</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      If you have any questions, concerns, or requests regarding these Terms of Service, 
                      please contact us:
                    </p>
                    <div className="card bg-alabaster">
                      <h3 className="text-lg font-semibold text-charcoal mb-4">Abacies Logiciels</h3>
                      <div className="space-y-3">
                        <p className="flex items-start">
                          <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>
                            123 Technology Drive<br />
                            Silicon Valley, CA 94025<br />
                            United States
                          </span>
                        </p>
                        <p className="flex items-center">
                          <svg className="w-5 h-5 text-primary-red mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>legal@abacies.com</span>
                        </p>
                        <p className="flex items-center">
                          <svg className="w-5 h-5 text-primary-red mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span>+1 (555) 123-4567</span>
                        </p>
                      </div>
                    </div>
                    <p>
                      For general inquiries about our services, please visit our{' '}
                      <Link to="/contact" className="text-primary-red hover:underline font-semibold">
                        Contact Page
                      </Link>
                      .
                    </p>
                  </div>
                </div>

                {/* Additional Legal Information */}
                <div className="mt-12 p-6 bg-alabaster rounded-xl">
                  <h3 className="text-lg font-semibold text-charcoal mb-3">Additional Legal Terms</h3>
                  <div className="space-y-2 text-sm text-titanium">
                    <p>
                      <strong>Severability:</strong> If any provision of these Terms is held to be invalid or 
                      unenforceable, such provision shall be struck and the remaining provisions shall be enforced.
                    </p>
                    <p>
                      <strong>Waiver:</strong> No waiver of any term of these Terms shall be deemed a further or 
                      continuing waiver of such term or any other term.
                    </p>
                    <p>
                      <strong>Entire Agreement:</strong> These Terms, together with any Service Agreements and 
                      referenced policies, constitute the entire agreement between you and Abacies regarding the 
                      use of our services.
                    </p>
                    <p>
                      <strong>Assignment:</strong> You may not assign or transfer these Terms without our prior 
                      written consent. Abacies may assign these Terms without restriction.
                    </p>
                    <p>
                      <strong>Force Majeure:</strong> Neither party shall be liable for any failure or delay in 
                      performance due to circumstances beyond their reasonable control.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-4">
              Questions About Our Terms?
            </h2>
            <p className="text-titanium mb-8">
              Our legal team is here to help clarify any aspects of our Terms of Service
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Contact Legal Team
              </Link>
              <Link to="/legal/privacy" className="btn-secondary">
                View Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Print/Download Section */}
      <section className="py-12 bg-white border-t border-platinum">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-titanium mb-4">
              Need a copy of these terms for your records?
            </p>
            <button 
              onClick={() => window.print()} 
              className="inline-flex items-center text-primary-red hover:underline"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print or Save as PDF
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default TermsOfService;  